import { ReactElement, Fragment, cloneElement } from 'react';

import { IconProps } from '@components/icons';
import { IconAskAI } from '@components/icons/ask-ai';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconBranch } from '@components/icons/branch';
import { IconCondition } from '@components/icons/condition';
import { IconDate } from '@components/icons/date';
import { IconDocumentCreate } from '@components/icons/document-create';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconFormatter } from '@components/icons/formatter';
import { IconHourglass } from '@components/icons/hourglass';
import { IconCode } from '@components/icons/icon-code';
import { IconWaitCondition } from '@components/icons/icon-wait-condition';
import { IconIntegration } from '@components/icons/integration';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconLoopExit } from '@components/icons/loop-exit';
import { IconLoopRestart } from '@components/icons/loop-restart';
import { IconMerge } from '@components/icons/merge';
import { IconSms } from '@components/icons/phone-sms';
import { IconPlaceholder } from '@components/icons/placeholder';
import { IconPlay } from '@components/icons/play';
import { IconReplace } from '@components/icons/replace';
import { IconTextAnonymize } from '@components/icons/text-anonymize';
import { IconTextExtract } from '@components/icons/text-extract';
import { IconTrigger } from '@components/icons/trigger';
import { IconWebhook } from '@components/icons/webhook';

import { ACTION_TYPE } from 'api/types';

export type ActionIconProps = Pick<IconProps, 'color' | 'size'> & {
  action: ACTION_TYPE;
};

export const ActionIcon = ({ action, color, size }: ActionIconProps) => {
  const iconElement = actionIcons[action];

  if (!iconElement) {
    console.error(`Unable to find icon for action: ${action}`);
    return null;
  }

  if (color === undefined && size === undefined) {
    return iconElement;
  }

  // Clone the element with new props if color or size is provided
  if (color || size) {
    return cloneElement(iconElement, {
      ...iconElement.props,
      color: color || iconElement.props.color,
      size: size || iconElement.props.size,
    });
  }

  return iconElement;
};

export const actionIcons: Record<ACTION_TYPE, ReactElement> = {
  ask_ai: <IconAskAI />,
  ask_ai_agent: <IconAskAI />,
  calendar_event: <IconDate color="elementGreen100" />,
  code: <IconCode color="elementIndigo100" />,
  combo_send_form: <IconForm color="elementBlue100" />,
  condition: <IconCondition color="teal100" />,
  condition_v2: <IconCondition color="teal100" />,
  convert_document: <IconReplace color="elementBlue100" />,
  create_document: <IconDocumentCreate color="elementBlue100" />,
  formatter: <IconFormatter color="elementGreen200" />,
  integration: <IconIntegration color="elementPurpleInvert" />,
  webhook: <IconWebhook color="elementGreen200" />,
  journey_branch: <IconBranch color="teal100" />,
  journey_merge: <IconMerge color="elementOrange100" />,
  journey_start: <IconPlay color="elementGreen100" />,
  journey_triggers: <IconTrigger color="elementBlue100" />,
  placeholder: <IconPlaceholder color="elementGreen200" />,
  send_email: <IconEmail color="elementGreen100" />,
  send_sms: <IconSms color="elementGreen100" />,
  journey_loop: <IconLoopRestart color="teal100" />,
  break_loop: <IconLoopExit color="elementRed100" />,
  wait_for_time: <IconHourglass color="elementOrange100" />,
  wait_for_attribute_change: <IconWaitCondition color="elementOrange100" />,
  wait_until_fixed_date: <IconHourglass color="elementOrange100" />,
  wait_until_relative_date: <IconHourglass color="elementOrange100" />,
  update_fields: <IconAttributeJourney color="elementPurpleInvert" />,
  add_to_journey: <IconJourneyItem color="teal100" />,
  update_traveller: <IconAttributeTraveller color="elementPurpleInvert" />,
  anonymize_text: <IconTextAnonymize color="elementIndigo100" />,
  extract_text: <IconTextExtract color="elementIndigo100" />,

  instantiate_form: <Fragment />,
  ghost: <Fragment />,
  hidden: <Fragment />,
  branch_base: <Fragment />,
};
